/* eslint-disable no-throw-literal */
import React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { useImmer } from "use-immer";
import {
  convertDate,
  errorMessage,
  printFileHelper,
  saveFileHelper,
  successMessage,
} from "../../../utils/helper";
import {
  downloadClientAccountStatementApi,
  downloadOnDemandStatementApi,
  getClientAccountStatementApi,
  getOnDemandStatementApi,
  sendClientAccountStatementApi,
  sendOnDemandStatementApi,
} from "../api/clientAccountApi";
import moment from "moment";

export const useClientAccountStatementDetails = () => {
  const { statementId, clientId } = useParams();
  const location = useLocation();
  const [searchPrams] = useSearchParams();
  const isStatement = Boolean(statementId);
  const isStoreAdmin = location.pathname.includes("store-admin");

  const [state, setState] = useImmer({
    statement: {},
    detailsLoading: true,
    isStatementPrinting: false,
    isEmailSending: false,
    isOpenSendDialog: false,
  });

  // ============================================================= API SECTION ==================================================
  const downloadOrPrintClientAccountStatement = async ({
    clientId,
    statementId,
    action,
  }) => {
    triggerPrintOrDownloadButtonLoading(true);
    try {
      const response = await downloadClientAccountStatementApi({
        clientId,
        statementId,
      });
      if (response.status === 200) {
        if (action === "print") {
          printFileHelper(response.data);
        } else {
          successMessage("Statement downloaded successfully");
          saveFileHelper({
            response,
            fileName: `Statement_${state.statement.billing_cycle_from}_to_${state.statement.billing_cycle_to}`,
          });
        }
      } else {
        throw {
          response: {
            data: {
              message: "Failed to fetch the statement",
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ?? "Failed to fetch the statement";

      errorMessage(message);
    } finally {
      triggerPrintOrDownloadButtonLoading(false);
    }
  };

  const downloadOrPrintOnDemandStatement = async ({
    clientId,
    params,
    action,
  }) => {
    triggerPrintOrDownloadButtonLoading(true);
    try {
      const response = await downloadOnDemandStatementApi({
        clientId,
        params,
      });
      if (response.status === 200) {
        if (action === "print") {
          printFileHelper(response.data);
        } else {
          successMessage("Statement downloaded successfully");
          saveFileHelper({
            response,
            fileName: `Statement_${state.statement.billing_cycle_from}_to_${state.statement.billing_cycle_to}`,
          });
        }
      } else {
        throw {
          response: {
            data: {
              message: "Failed to fetch the statement",
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ?? "Failed to fetch the statement";

      errorMessage(message);
    } finally {
      triggerPrintOrDownloadButtonLoading(false);
    }
  };

  const getOnDemandStatement = async ({ clientId, params }) => {
    triggerFormLoading(true);
    try {
      const response = await getOnDemandStatementApi({ clientId, params });
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.statement = data;
        });
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to fetch the demand statement list";

      errorMessage(message);
    } finally {
      triggerFormLoading(false);
    }
  };

  const getClientAccountStatement = async ({ statementId }) => {
    triggerFormLoading(true);
    try {
      const response = await getClientAccountStatementApi({ statementId });
      const { success, message, data } = response;
      if (success) {
        setState((draft) => {
          draft.statement = data;
        });
      } else {
        throw {
          response: {
            data: {
              message,
            },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to fetch the demand statement list";

      errorMessage(message);
    } finally {
      triggerFormLoading(false);
    }
  };

  const sendStatement = async ({ emails, clientId, statementId }) => {
    triggerSendEmailButtonLoading(true);
    try {
      const response = await sendClientAccountStatementApi({
        clientId,
        statementId,
        emails,
      });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        handleToggleSendEmailModal(false);
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to fetch the demand statement list";

      errorMessage(message);
    } finally {
      triggerSendEmailButtonLoading(false);
    }
  };

  const sendOnDemandStatement = async ({
    emails,
    clientId,
    start_date,
    end_date,
  }) => {
    triggerSendEmailButtonLoading(true);
    try {
      const response = await sendOnDemandStatementApi({
        clientId,
        data: {
          emails,
          start_date,
          end_date,
        },
      });
      const { success, message } = response;
      if (success) {
        successMessage(message);
        handleToggleSendEmailModal(false);
      } else {
        throw {
          response: {
            data: { message },
          },
        };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ??
        "Failed to fetch the demand statement list";

      errorMessage(message);
    } finally {
      triggerSendEmailButtonLoading(false);
    }
  };

  // ============================================================ UTIL FUNCTION SECTION =============================================
  const triggerSendEmailButtonLoading = (loading) => {
    setState((draft) => {
      draft.isEmailSending = loading;
    });
  };

  const triggerFormLoading = (loading) => {
    setState((draft) => {
      draft.detailsLoading = loading;
    });
  };

  const triggerPrintOrDownloadButtonLoading = (status) => {
    setState((draft) => {
      draft.isStatementPrinting = status;
    });
  };

  const handleToggleSendEmailModal = (open) => {
    setState((draft) => {
      draft.isOpenSendDialog = open;
    });
  };

  const handleDownloadOrPrintStatement = async (action) => {
    if (isStatement) {
      await downloadOrPrintClientAccountStatement({
        clientId,
        statementId,
        action,
      });
    } else {
      const params = getSearchParams();
      await downloadOrPrintOnDemandStatement({ clientId, action, params });
    }
  };

  const getSearchParams = () => {
    const dateRange = new URLSearchParams(location.search);
    const start_date = dateRange.get("start_date");
    const end_date = dateRange.get("end_date");

    return { start_date, end_date };
  };

  const handleSubmitTheEmail = (emails) => {
    if (isStatement) {
      sendStatement({ emails, clientId, statementId });
    } else {
      const params = getSearchParams();
      sendOnDemandStatement({ emails, clientId, ...params });
    }
  };

  const navigateToTransactionDetails = (row) => {
    const transactionType = row.transaction_type.toLowerCase();

    if (transactionType === "purchase") {
      const url = isStoreAdmin
        ? `/store-admin/orders/details/${row?.order_id}`
        : `/orders/details/${row?.order_id}`;
      return url;
    } else if (transactionType === "refund") {
      const url = isStoreAdmin
        ? `/store-admin/refund/details/${row.refund_id}`
        : `/refund/details/${row.refund_id}`;
      return url;
    } else if (["payment", "payment revert"]) {
      const url = isStoreAdmin
        ? `/store-admin/client-account-v2/details/${row.client_account_id}/transactions/${row.id}`
        : `/client-account-v2/details/${row.client_account_id}/transactions/${row.id}`;
      return url;
    }
  };

  const getTransactionDetail = (row) => {
    switch (row.transaction_type.toLowerCase()) {
      case "purchase":
        return `Order ${row.order_number}`;
      case "payment":
        return row?.settlement_date
          ? `Payment, done on ${convertDate({
              date: row.settlement_date,
              format: "DD MMM YYYY",
            })}`
          : "Payment";
      default:
        return row.transaction_type;
    }
  };

  React.useEffect(() => {
    const params = getSearchParams();

    if (params.start_date && params.end_date) {
      getOnDemandStatement({
        clientId,
        params,
      });
    } else {
      getClientAccountStatement({ statementId });
    }
  }, []);

  return {
    state,
    searchPrams,
    handleDownloadOrPrintStatement,
    handleSubmitTheEmail,
    handleToggleSendEmailModal,
    navigateToTransactionDetails,
    getTransactionDetail,
  };
};
